// Phōkii theme system — extracted from DailyFocusTool.js (refactor phase 1).
// Owns the theme definitions, the context/provider, and the two small
// theme-aware components (ThemeSwitcher, PhoIcon).
import React, { createContext, useContext, useState, useEffect } from 'react';
import { Sun, Moon, Leaf, Coffee, Zap, Aperture, Focus } from 'lucide-react';

const ThemeContext = createContext();

const themes = {
  // 1. Bear — the comp's warm bear-light, judged best of the Oct 2026 design
  // round. Cream paper, teal accent, coral reserved for the star (✦ only).
  // (The key is Paul's own word for it; "Paper" was already taken by #8.)
  bear: {
    name: 'Bear',
    icon: Coffee,
    colors: {
      primary: '#0C6F78',
      primaryHover: '#095A62',
      secondary: '#6D4FC2',
      accent: '#0C6F78',
      background: '#F6F1E7',
      surface: '#FFFDF8',
      surfaceHover: '#F0E9DA',
      text: '#2A2622',
      textSecondary: '#6E6659',
      textMuted: '#6E6659',
      border: '#E2DACA',
      borderLight: '#EFE9DC',
      success: '#65632A',
      warning: '#8A6A10',
      error: '#A23B2A',
      pho: '#B2381F',
      phoLight: '#F9E8E3',
      phoBorder: '#E5BDB2',
      landed: '#F3ECDD',
      faint: '#716757',
      gridLine: 'rgba(110,102,89,.09)'
    },
    font: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'Segoe UI', Roboto, sans-serif"
  },
  // 2. Neon — the comp's dark control room. Near-black, cyan/amber/green
  // state lights, the star in warm cream.
  neon: {
    name: 'Neon',
    icon: Zap,
    colors: {
      primary: '#62d4ff',
      primaryHover: '#4cc4f2',
      secondary: '#B08CFA',
      accent: '#62d4ff',
      background: '#0a0b0e',
      surface: '#13161d',
      surfaceHover: '#191d26',
      text: '#e8ebf2',
      textSecondary: '#98a1b4',
      textMuted: '#98a1b4',
      border: '#232834',
      borderLight: '#2b3140',
      success: '#55e6a0',
      warning: '#ffc857',
      error: '#ff7a7a',
      pho: '#f3efe2',
      phoLight: '#1c1f28',
      phoBorder: '#3a4150',
      landed: '#141b16',
      faint: '#7e88a3',
      gridLine: 'rgba(130,150,190,.055)'
    },
    font: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'Segoe UI', Roboto, sans-serif"
  },
  // 3. Laguna — Phōkii's own. Clean teal on white. Confident, calm, modern.
  default: {
    name: 'Laguna',
    icon: Sun,
    colors: {
      primary: '#0d9488',
      primaryHover: '#0f766e',
      secondary: '#6366f1',
      accent: '#14b8a6',
      background: '#ffffff',
      surface: '#f8fafc',
      surfaceHover: '#f1f5f9',
      text: '#0f172a',
      textSecondary: '#0d9488',
      textMuted: '#64748b',
      border: '#e2e8f0',
      borderLight: '#f1f5f9',
      success: '#10b981',
      warning: '#f59e0b',
      error: '#ef4444',
      pho: '#0d9488',
      phoLight: '#f0fdfa',
      phoBorder: '#99f6e4'
    },
    font: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
  },
  // 2. Midnight — Linear-inspired. Dark navy, electric blue. Dense, focused, techy.
  midnight: {
    name: 'Midnight',
    icon: Moon,
    colors: {
      primary: '#5b8dee',
      primaryHover: '#4a7de8',
      secondary: '#7c3aed',
      accent: '#38bdf8',
      background: '#0d1117',
      surface: '#161b22',
      surfaceHover: '#1f2937',
      text: '#e6edf3',
      textSecondary: '#8d96a0',
      textMuted: '#6e7681',
      border: '#21262d',
      borderLight: '#30363d',
      success: '#3fb950',
      warning: '#d29922',
      error: '#f85149',
      pho: '#5b8dee',
      phoLight: '#1a2744',
      phoBorder: '#2d4a8a'
    },
    font: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif'
  },

  // 3. Chalk — Notion-inspired. Warm white, editorial, minimal color. For writers and thinkers.
  chalk: {
    name: 'Chalk',
    icon: Leaf,
    colors: {
      primary: '#2383e2',
      primaryHover: '#1a6db8',
      secondary: '#e03e3e',
      accent: '#0f7b6c',
      background: '#ffffff',
      surface: '#fbfbfa',
      surfaceHover: '#f7f6f3',
      text: '#37352f',
      textSecondary: '#6e6d68',
      textMuted: '#9b9a97',
      border: '#e9e9e7',
      borderLight: '#f7f6f3',
      success: '#448361',
      warning: '#d9730d',
      error: '#e03e3e',
      pho: '#2383e2',
      phoLight: '#f2f8fd',
      phoBorder: '#b8d9f5'
    },
    font: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
  },

  // 4. Forest — Things 3 / Bear inspired. Warm earth tones, organic, calm. Great for personal use.
  forest: {
    name: 'Forest',
    icon: Leaf,
    colors: {
      primary: '#3d6b4f',
      primaryHover: '#2e5239',
      secondary: '#8b6914',
      accent: '#5c8a6a',
      background: '#fafaf8',
      surface: '#f4f3ef',
      surfaceHover: '#eceae4',
      text: '#2c2b28',
      textSecondary: '#3d6b4f',
      textMuted: '#7a7163',
      border: '#dedad2',
      borderLight: '#f0ede6',
      success: '#3d6b4f',
      warning: '#a07520',
      error: '#c0392b',
      pho: '#3d6b4f',
      phoLight: '#edf3ef',
      phoBorder: '#a8c4b0'
    },
    font: 'Georgia, "Times New Roman", serif'
  },

  // 5. Arctic — Apple / Things 3 iOS inspired. Pure white, SF blue, impeccable spacing. Premium feel.
  arctic: {
    name: 'Arctic',
    icon: Sun,
    colors: {
      primary: '#007aff',
      primaryHover: '#006be6',
      secondary: '#5856d6',
      accent: '#34c759',
      background: '#ffffff',
      surface: '#f2f2f7',
      surfaceHover: '#e5e5ea',
      text: '#000000',
      textSecondary: '#3c3c43',
      textMuted: '#8e8e93',
      border: '#c6c6c8',
      borderLight: '#f2f2f7',
      success: '#34c759',
      warning: '#ff9500',
      error: '#ff3b30',
      pho: '#007aff',
      phoLight: '#ebf4ff',
      phoBorder: '#a3c8ff'
    },
    font: '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Helvetica Neue", sans-serif'
  },

  // 6. Obsidian — Bear dark / Craft dark inspired. Rich dark with lavender accent. For deep work sessions.
  obsidian: {
    name: 'Obsidian',
    icon: Moon,
    colors: {
      primary: '#9b87f5',
      primaryHover: '#8b76f0',
      secondary: '#f472b6',
      accent: '#c084fc',
      background: '#1e1e2e',
      surface: '#27273a',
      surfaceHover: '#313145',
      text: '#cdd6f4',
      textSecondary: '#a6adc8',
      textMuted: '#6c7086',
      border: '#45475a',
      borderLight: '#313244',
      success: '#a6e3a1',
      warning: '#f9e2af',
      error: '#f38ba8',
      pho: '#9b87f5',
      phoLight: '#2a2040',
      phoBorder: '#6c4fa8'
    },
    font: '"JetBrains Mono", "Fira Code", "SF Mono", monospace'
  },

  // 7. Coral — Todoist-inspired. Energetic red-coral, clean white. Action-oriented and decisive.
  coral: {
    name: 'Coral',
    icon: Zap,
    colors: {
      primary: '#db4035',
      primaryHover: '#c0392b',
      secondary: '#4073ff',
      accent: '#ff8c42',
      background: '#ffffff',
      surface: '#fafafa',
      surfaceHover: '#f5f5f5',
      text: '#202020',
      textSecondary: '#555555',
      textMuted: '#999999',
      border: '#e0e0e0',
      borderLight: '#f5f5f5',
      success: '#299438',
      warning: '#e09500',
      error: '#db4035',
      pho: '#db4035',
      phoLight: '#fdf3f2',
      phoBorder: '#f5b8b5'
    },
    font: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif'
  },

  // 8. Paper — borrowed from the Artifact Review tool. Warm editorial cream,
  // ink hierarchy, 800-weight tight-tracked display type. Reads like print.
  paper: {
    name: 'Paper',
    icon: Coffee,
    colors: {
      primary: '#2557f0',
      primaryHover: '#1e46c8',
      secondary: '#9333ea',
      accent: '#f04a1d',
      background: '#f2ede2',
      surface: '#faf7ef',
      surfaceHover: '#eae3d3',
      text: '#17140d',
      textSecondary: '#5c5443',
      textMuted: '#a0967f',
      border: '#d9d0bc',
      borderLight: '#eae3d3',
      success: '#13a244',
      warning: '#eda305',
      error: '#f04a1d',
      pho: '#2557f0',
      phoLight: '#dee7fd',
      phoBorder: '#b3c6f9'
    },
    font: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'Segoe UI', Roboto, sans-serif",
    headspace: {
      displayWeight: '800',
      displayTracking: '-0.03em',
      noteFontSize: '17px',
      noteLineHeight: '1.75',
      uiLineHeight: '1.55',
      fontFeatures: "'cv11', 'ss01'"
    }
  },

  // 9. Paper Dark — the same editorial warmth on near-black charcoal.
  paperDark: {
    name: 'Paper Dark',
    icon: Aperture,
    colors: {
      primary: '#7e97ff',
      primaryHover: '#93a8ff',
      secondary: '#c084fc',
      accent: '#ff7a52',
      background: '#141210',
      surface: '#1c1915',
      surfaceHover: '#26221b',
      text: '#f2edde',
      textSecondary: '#c4bba2',
      textMuted: '#96917e',
      border: '#3d372c',
      borderLight: '#2c2820',
      success: '#4ade80',
      warning: '#ffc243',
      error: '#ff7a52',
      pho: '#7e97ff',
      phoLight: '#1f2b52',
      phoBorder: '#3a4a80'
    },
    font: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'Segoe UI', Roboto, sans-serif",
    headspace: {
      displayWeight: '800',
      displayTracking: '-0.03em',
      noteFontSize: '17px',
      noteLineHeight: '1.75',
      uiLineHeight: '1.55',
      fontFeatures: "'cv11', 'ss01'"
    }
  },

  // 10. Cockpit — dense ops headspace. The whole UI scales down ~12%, tight
  // leading, cool grays: maximum actions per screen for triage days.
  cockpit: {
    name: 'Cockpit',
    icon: Zap,
    colors: {
      primary: '#0969da',
      primaryHover: '#0550ae',
      secondary: '#8250df',
      accent: '#1a7f37',
      background: '#ffffff',
      surface: '#f6f8fa',
      surfaceHover: '#eaeef2',
      text: '#1f2328',
      textSecondary: '#57606a',
      textMuted: '#6e7781',
      border: '#d0d7de',
      borderLight: '#eaeef2',
      success: '#1a7f37',
      warning: '#9a6700',
      error: '#cf222e',
      pho: '#0969da',
      phoLight: '#ddf4ff',
      phoBorder: '#80ccff'
    },
    font: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
    headspace: {
      uiScale: '14px',
      displayWeight: '650',
      displayTracking: '-0.01em',
      noteFontSize: '13.5px',
      noteLineHeight: '1.5',
      uiLineHeight: '1.35'
    }
  },

  // 11. Studio — big calm writing room. Whole UI scales up, generous air,
  // serif voice, near-monochrome warm gray. For deep-work writing days.
  studio: {
    name: 'Studio',
    icon: Focus,
    colors: {
      primary: '#7c5c33',
      primaryHover: '#66491f',
      secondary: '#46586a',
      accent: '#8a5a2b',
      background: '#fcfcfa',
      surface: '#f6f5f1',
      surfaceHover: '#efede7',
      text: '#1c1b18',
      textSecondary: '#57534a',
      textMuted: '#8a867c',
      border: '#e7e4dc',
      borderLight: '#f0eee8',
      success: '#3f6212',
      warning: '#a16207',
      error: '#b91c1c',
      pho: '#7c5c33',
      phoLight: '#f5efe4',
      phoBorder: '#dbc9a8'
    },
    font: "'Iowan Old Style', 'Palatino', Georgia, 'Times New Roman', serif",
    headspace: {
      uiScale: '17.5px',
      displayWeight: '700',
      displayTracking: '-0.015em',
      noteFontSize: '19px',
      noteLineHeight: '1.85',
      uiLineHeight: '1.6'
    }
  },

  // 12. Poster — the brand's own palette, printed loud. Flat saturated blocks
  // on cream with black ink: the one theme where the books shout.
  poster: {
    name: 'Poster',
    icon: Zap,
    colors: {
      primary: '#2f5cf0',
      primaryHover: '#2347c8',
      secondary: '#9333ea',
      accent: '#f04a1d',
      background: '#efe9dc',
      surface: '#f8f4ea',
      surfaceHover: '#e4dcc8',
      text: '#14110c',
      textSecondary: '#4a443a',
      textMuted: '#877e6c',
      border: '#d3c9b4',
      borderLight: '#e4dcc8',
      success: '#10a24a',
      warning: '#f0a500',
      error: '#f04a1d',
      pho: '#f04a1d',
      phoLight: '#fde3dc',
      phoBorder: '#f7b9a8'
    },
    // saturated covers — the reason this theme exists
    coverMix: { a: '58%', b: '72%', c: '86%', acc: '92%' },
    font: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'Segoe UI', Roboto, sans-serif",
    headspace: {
      displayWeight: '800',
      displayTracking: '-0.035em',
      noteFontSize: '16.5px',
      noteLineHeight: '1.7',
      uiLineHeight: '1.5',
      fontFeatures: "'cv11', 'ss01'"
    }
  }
};

// Theme Provider Component
const ThemeProvider = ({ children }) => {
  // Lazy init from storage: an effect-based read raced the apply-effect's
  // write-back and could overwrite the saved theme with 'default'.
  const [currentTheme, setCurrentTheme] = useState(
    () => {
      const saved = localStorage.getItem('daily-focus-theme');
      return saved && themes[saved] ? saved : 'default';
    }
  );

  useEffect(() => {
    const theme = themes[currentTheme];
    const root = document.documentElement;
    
    // Apply CSS custom properties
    Object.entries(theme.colors).forEach(([key, value]) => {
      root.style.setProperty(`--color-${key}`, value);
    });

    // "Landed" warm-paper tint for completed cards (the Settle). Themes may
    // define their own colors.landed; otherwise derive a warm tint from the
    // theme's surface so it works across all headspaces. Never green.
    const cm = theme.coverMix || {};
    root.style.setProperty('--pk-mixA', cm.a || '20%');
    root.style.setProperty('--pk-mixB', cm.b || '28%');
    root.style.setProperty('--pk-mixC', cm.c || '38%');
    root.style.setProperty('--pk-mixAcc', cm.acc || '62%');

    root.style.setProperty('--color-landed',
      theme.colors.landed || 'color-mix(in srgb, #b8842c 8%, var(--color-surface))');
    // the mock's paper and third ink (Oct 9 2026): themes without their own
    // values derive them, so switching themes never leaves stale vars
    root.style.setProperty('--color-gridLine',
      theme.colors.gridLine || 'color-mix(in srgb, var(--color-text) 5%, transparent)');
    root.style.setProperty('--color-faint',
      theme.colors.faint || theme.colors.textMuted);
    
    // Apply font
    root.style.setProperty('--font-main', theme.font);
    
    // Apply base font size if specified
    if (theme.baseFontSize) {
      root.style.setProperty('--font-size-base', theme.baseFontSize);
      document.body.style.fontSize = theme.baseFontSize;
    } else {
      root.style.setProperty('--font-size-base', '1rem');
      document.body.style.fontSize = '1rem';
    }

    // Headspace: themes can change typography and density, not just colors.
    // uiScale sets the root font-size, so every rem-based Tailwind size in the
    // app scales with it. The rest feed CSS vars consumed in the stylesheets.
    const hs = {
      uiScale: '16px',
      displayFont: theme.font,
      displayWeight: '600',
      displayTracking: '-0.01em',
      noteFontSize: '13px',
      noteLineHeight: '1.6',
      uiLineHeight: '1.5',
      fontFeatures: 'normal',
      ...(theme.headspace || {}),
    };
    root.style.fontSize = hs.uiScale;
    root.style.setProperty('--font-display', hs.displayFont);
    root.style.setProperty('--weight-display', hs.displayWeight);
    root.style.setProperty('--tracking-display', hs.displayTracking);
    root.style.setProperty('--fs-note', hs.noteFontSize);
    root.style.setProperty('--lh-note', hs.noteLineHeight);
    root.style.setProperty('--lh-ui', hs.uiLineHeight);
    document.body.style.fontFeatureSettings = hs.fontFeatures;

    // Apply background and font to body
    document.body.style.backgroundColor = theme.colors.background;
    document.body.style.color = theme.colors.text;
    document.body.style.fontFamily = theme.font;
    
    // Save to localStorage
    localStorage.setItem('daily-focus-theme', currentTheme);
  }, [currentTheme]);

  const switchTheme = (themeName) => {
    setCurrentTheme(themeName);
  };

  return (
    <ThemeContext.Provider value={{ 
      currentTheme, 
      switchTheme, 
      themes,
      theme: themes[currentTheme]
    }}>
      {children}
    </ThemeContext.Provider>
  );
};

// Hook to use theme
const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

// Theme Switcher Component
const ThemeSwitcher = () => {
  const { currentTheme, switchTheme, themes } = useTheme();
  return (
    <div>
      <p className="text-[10px] uppercase tracking-widest text-gray-400 mb-2 px-1">Theme</p>
      <div className="grid grid-cols-6 gap-1.5">
        {Object.entries(themes).map(([key, theme]) => (
          <button
            key={key}
            onClick={() => switchTheme(key)}
            title={theme.name}
            className="relative w-6 h-6 rounded-full border-2 transition-transform hover:scale-110 focus:outline-none"
            style={{
              backgroundColor: theme.colors.primary,
              borderColor: currentTheme === key ? theme.colors.primary : 'transparent',
              boxShadow: currentTheme === key ? `0 0 0 2px white, 0 0 0 3px ${theme.colors.primary}` : 'none',
            }}
          />
        ))}
      </div>
      <p className="text-[10px] text-gray-400 mt-1.5 px-1">{themes[currentTheme].name}</p>
    </div>
  );
};

const PhoIcon = ({ active }) => (
  <Focus className={`w-4 h-4 themed-text-muted hover:themed-text-secondary`} />
);

export { ThemeContext, themes, ThemeProvider, useTheme, ThemeSwitcher, PhoIcon };
